import dayjs from 'dayjs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useGetApiV1ExamRevisionGrid } from '@/shared/api/generated';
import type { ClassroomDisciplineBotExamGridItemRepositoryResponse } from '@/shared/api/generated/types/ClassroomDisciplineBotExamGridItemRepositoryResponse';
import { unwrapPagedResponse } from '@/shared/utils/unwrapPagedResponse';
import type {
  RevisionGridFilters,
  RevisionGridHookReturn,
  RevisionGridItem,
  RevisionSort,
} from '../types/revision';

const PAGE_SIZE = 20;

const EMPTY_FILTERS: RevisionGridFilters = {
  disciplineId: null,
  classStudentId: null,
  date: '',
  page: 1,
};

/** Formata a data-time ISO retornada pelo backend em `dd/mm/aaaa` para exibição. */
function formatDate(value: string | undefined): string {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '';
}

/** Mapeia um item do DTO gerado para o modelo de exibição da grade. */
function toGridItem(dto: ClassroomDisciplineBotExamGridItemRepositoryResponse): RevisionGridItem {
  return {
    id: dto.id ?? 0,
    disciplineName: dto.disciplineName ?? '',
    date: formatDate(dto.date),
    classCodes: dto.classStudentCodes ?? [],
    statusId: dto.statusId ?? 0,
    statusText: dto.statusText ?? '',
    canPublish: dto.canButtonPublish ?? false,
    canTranslate: dto.canButtonTranslate ?? false,
    canReview: dto.canButtonReview ?? false,
    canView: dto.canButtonView ?? false,
  };
}

/** Extrai o `totalCount` quando o backend responde com um envelope paginado. */
function readTotalCount(raw: unknown): number | undefined {
  if (raw && typeof raw === 'object' && 'totalCount' in raw) {
    const total = (raw as { totalCount?: unknown }).totalCount;
    if (typeof total === 'number') return total;
  }
  return undefined;
}

/** Comparador de itens da grade para uma coluna ordenável (aplicado à página atual). */
function compareBy(a: RevisionGridItem, b: RevisionGridItem, id: string): number {
  switch (id) {
    case 'date':
      return dayjs(a.date, 'DD/MM/YYYY').valueOf() - dayjs(b.date, 'DD/MM/YYYY').valueOf();
    case 'statusId':
      return a.statusId - b.statusId;
    default:
      return a.disciplineName.localeCompare(b.disciplineName, 'pt-BR');
  }
}

/**
 * Hook para a listagem de provas disponíveis para revisão.
 *
 * Consome `GET /api/v1/exam/revision/grid` com filtros e paginação server-side.
 * A ordenação por coluna é aplicada apenas à página carregada (o endpoint não
 * expõe parâmetro de ordenação — os dados já vêm ordenados por data desc).
 *
 * @returns Provas da página atual, filtros, paginação e estados de loading/erro.
 *
 * @example
 * ```tsx
 * const { items, isLoading, applyFilters } = useRevisionGrid();
 * ```
 */
/** Compara duas configurações de filtro pelos valores relevantes para a consulta. */
function sameFilters(a: RevisionGridFilters, b: RevisionGridFilters): boolean {
  return (
    a.disciplineId === b.disciplineId &&
    a.classStudentId === b.classStudentId &&
    a.date === b.date &&
    a.page === b.page
  );
}

export function useRevisionGrid(): RevisionGridHookReturn {
  const [filters, setFilters] = useState<RevisionGridFilters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<RevisionGridFilters>(EMPTY_FILTERS);
  const [sorting, setSortingState] = useState<RevisionSort[]>([]);
  const [resetKey, setResetKey] = useState(0);

  const query = useGetApiV1ExamRevisionGrid({
    ...(appliedFilters.disciplineId !== null ? { disciplineId: appliedFilters.disciplineId } : {}),
    ...(appliedFilters.classStudentId !== null
      ? { classStudentId: appliedFilters.classStudentId }
      : {}),
    ...(appliedFilters.date ? { date: appliedFilters.date } : {}),
    pageNumber: appliedFilters.page,
    pageSize: PAGE_SIZE,
  });

  // Refs para evitar closures obsoletas nos callbacks estáveis.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const appliedRef = useRef(appliedFilters);
  appliedRef.current = appliedFilters;
  const refetchRef = useRef(query.refetch);
  refetchRef.current = query.refetch;

  const rawData = query.data as unknown;
  const totalItems = readTotalCount(rawData);

  const items = useMemo(() => {
    const mapped =
      unwrapPagedResponse<ClassroomDisciplineBotExamGridItemRepositoryResponse>(rawData).map(
        toGridItem,
      );
    const sort = sorting[0];
    if (!sort) return mapped;
    const sorted = [...mapped].sort((a, b) => compareBy(a, b, sort.id));
    return sort.desc ? sorted.reverse() : sorted;
  }, [rawData, sorting]);

  const hasNextPage =
    totalItems !== undefined
      ? appliedFilters.page * PAGE_SIZE < totalItems
      : items.length === PAGE_SIZE;

  const applyFilters = useCallback(() => {
    const next: RevisionGridFilters = { ...filtersRef.current, page: 1 };
    setFilters(next);
    setAppliedFilters(next);
    // Se os valores aplicados não mudaram, a queryKey é a mesma e o TanStack Query
    // não refaz a busca — força um refetch para o botão sempre "pesquisar".
    if (sameFilters(appliedRef.current, next)) {
      void refetchRef.current();
    }
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    // Remonta os campos de filtro para garantir a limpeza visual (data e autocompletes).
    setResetKey(k => k + 1);
  }, []);

  const setPage = useCallback((page: number) => {
    setAppliedFilters(prev => ({ ...prev, page }));
    setFilters(prev => ({ ...prev, page }));
  }, []);

  const setSorting = useCallback((next: RevisionSort[]) => {
    setSortingState(next);
  }, []);

  return {
    items,
    filters,
    setFilters,
    applyFilters,
    resetFilters,
    totalItems,
    hasNextPage,
    currentPage: appliedFilters.page,
    pageSize: PAGE_SIZE,
    setPage,
    sorting,
    setSorting,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
    resetKey,
  };
}
